import { browser } from '$app/environment';
import { navigating, page } from '$app/stores';
import {
  PUBLIC_BETWATCH_API_ENDPOINT,
  PUBLIC_STRIPE_SUBSCRIPTION_PREMIUM_PRICE_ID
} from '$env/static/public';
import type { User } from '$types/races';
import { derived, get, writable } from 'svelte/store';

/**
 * Creates a new user in the system with the provided name.
 * This function should be called when a new user needs to be registered.
 * It performs a mutation request to the Betwatch API to create the user.
 *
 * @param {string} name - The name of the user to be created.
 * @returns {Promise<void>} - A promise that resolves when the user is created.
 */
export async function createUser(name: string) {
  if (!browser) return;
  const access_token = get(page)?.data?.session?.access_token;
  if (!access_token) return;

  const user = get(page).data?.session?.user;
  if (!user) return;

  const response = await fetch(PUBLIC_BETWATCH_API_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + access_token
    },

    body: JSON.stringify({
      query: `mutation CreateUser($id: ID!, $name: String!, $email: String!) {
            createUser(id: $id, name: $name, email: $email) {
              id
              name
              email
            }
          }`,
      variables: {
        id: user.id,
        name: name,
        email: user.email
      }
    })
  });
  if (!response.ok) {
    throw new Error('Failed to update user alert config');
  }
}

/**
 * Checks if the user's email domain is one of the corporate domains.
 *
 * @param {User} user - The user object to check for corporate access.
 * @returns {boolean} - True if the user has corporate access, false otherwise.
 */
function hasCorporateAccess(user: User) {
  const corporateDomains = [
    'betwatch.com',
    'pointsbet.com',
    'pointsbet.com.au',
    'starsports.com.au'
  ];
  const userEmailDomain = user.email.split('@')[1];
  if (corporateDomains.includes(userEmailDomain)) {
    return true
  }

  // allow star sports domain users for now
  if (
    user.email === 'shaneciurleo@gmail.com' ||
    user.email === 'jupp.anthony@gmail.com'
  ) {
    return true;
  }

  return false;
}

export const lastAuthState = writable(false); // keep track of the last auth state so we can return it when navigating
export const isSubscribed = derived(
  [page, navigating],
  ([$page, $navigating]) => {
    if ($navigating) return get(lastAuthState);
    if (!browser) return false;
    if (!$page?.data?.user?.data?.user.id) {
      // sometimes this is null when navigating back to home page
      // so we will get a flash of the unauthenticated state

      return get(lastAuthState);
    } // no user

    // check corporate access
    if (hasCorporateAccess($page.data?.user?.data?.user)) {
      lastAuthState.set(true);
      return true;
    }

    // find the racing sub
    const subs =
      $page.data?.user?.data?.user?.billingInformation?.subscriptions.filter(
        (s) =>
          s.priceId === 'price_1MweB9AbQS6F657PVO7BiEEf' ||
          s.priceId === 'price_1Mq3QGAbQS6F657PNVSVGpE5' ||
          s.priceId === '' ||
          s.priceId === PUBLIC_STRIPE_SUBSCRIPTION_PREMIUM_PRICE_ID
      );
    // const subs =
    //   $page.data?.user?.data?.user?.billingInformation?.subscriptions;
    let sub = subs?.[0] as
      | User['billingInformation']['subscriptions'][0]
      | undefined;
    if (subs && (subs?.length ?? 0) > 1) {
      // console.error('User has more than one subscription');
      sub = subs.find(
        (s) =>
          s.status === 'active' ||
          s.status === 'trialing' ||
          s.status === 'past_due'
      );
    }

    if (!sub) {
      lastAuthState.set(false);
      return false;
    }
    if (sub.status === 'active') {
      lastAuthState.set(true);
      return true;
    } else if (sub.status === 'trialing') {
      lastAuthState.set(true);
      return true;
    } else if (sub.status === 'past_due') {
      // NOTE: this is a bit of a hack, but we'll consider the user subscribed if they're past due
      // because they can still access the app
      lastAuthState.set(true);
      return true;
    } else {
      lastAuthState.set(false);
      return false;
    }
  }
);

/**
 * Check if a specific user is subscribed
 * @param user  The user to check
 * @returns   True if the user is subscribed, false otherwise
 */
export const isUserSubscribed = (user: User) => {
  if (!user.id) return false; // no user

  // check for corporate access
  if (hasCorporateAccess(user)) return true;

  const subs = user?.billingInformation?.subscriptions.filter(
    (s) =>
      s.priceId === 'price_1MweB9AbQS6F657PVO7BiEEf' ||
      s.priceId === 'price_1Mq3QGAbQS6F657PNVSVGpE5' ||
      s.priceId === '' ||
      s.priceId === PUBLIC_STRIPE_SUBSCRIPTION_PREMIUM_PRICE_ID
  );

  const sub = subs?.[0];
  if (!sub) return false;
  if (sub.status === 'active') {
    return true;
  } else if (sub.status === 'trialing') {
    return true;
  } else if (sub.status === 'past_due') {
    // NOTE: this is a bit of a hack, but we'll consider the user subscribed if they're past due
    // because they can still access the app
    return true;
  } else {
    return false;
  }
};
